/* ═══════════════════════════════════════════════════════════════════════════
   Cube 3D — Get Started Quiz
   Web component: <get-started-quiz>
   Reads product data from #gs-product-data, runs a 6-step quiz, filters FDM
   printers on the hard answers (budget cap, size, multi-colour, enclosure),
   scores the rest and shows up to 3 picks. If the best match can't be bought
   today it stays, badged, next to the best printer that can.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── Quiz questions ───────────────────────────────────────────────────── */
  var QUESTIONS = [
    {
      id: 'budget',
      title: 'What\u2019s your budget?',
      hint: 'Pick one range',
      multi: false,
      cols: 1,
      options: [
        { value: 'u400',     label: 'Under $400',     desc: 'Entry-level printers' },
        { value: '400-600',  label: '$400 - $600',    desc: 'Great starter range' },
        { value: '600-1200', label: '$600 - $1,200',  desc: 'Premium performance' },
        { value: '1200+',    label: '$1,200+',        desc: 'Professional grade' }
      ]
    },
    {
      id: 'use',
      title: 'What will you mostly print?',
      hint: 'Pick one or more',
      multi: true,
      cols: 2,
      options: [
        { value: 'toys',        label: 'Toys & Figurines',     desc: 'Models, miniatures, display' },
        { value: 'functional',  label: 'Functional Parts',     desc: 'Tools, brackets, replacements' },
        { value: 'prototyping', label: 'Prototyping',          desc: 'Quick iterations, test fits' },
        { value: 'art',         label: 'Art & Decoration',     desc: 'Vases, sculptures, home décor' },
        { value: 'education',   label: 'Education / Learning', desc: 'School, STEM, kids' }
      ]
    },
    {
      id: 'multicolor',
      title: 'Do you want multi-color printing?',
      hint: 'Multi-color lets you print in several colors at once',
      multi: false,
      cols: 2,
      options: [
        { value: 'yes', label: 'Yes!',              desc: 'I want colorful prints right away' },
        { value: 'no',  label: 'No, single color is fine', desc: 'I can always upgrade later' }
      ]
    },
    {
      id: 'size',
      title: 'How big do you want to print?',
      hint: 'The maximum size of a single printed object',
      multi: false,
      cols: 1,
      options: [
        { value: 'small',  label: 'Small (up to 23 cm)',       desc: '' },
        { value: 'medium', label: 'Medium (24 \u2013 30 cm)', desc: '' },
        { value: 'large',  label: 'Large (31 cm+)',            desc: '' }
      ]
    },
    {
      id: 'materials',
      title: 'Do you plan to print with engineering materials?',
      hint: 'ABS, Nylon, Carbon Fiber need an enclosed printer',
      multi: false,
      cols: 1,
      options: [
        { value: 'yes',     label: 'Yes - ABS, Nylon, PA-CF', desc: 'I need an enclosed, heated chamber' },
        { value: 'no',      label: 'No - PLA & PETG are fine', desc: 'The most popular beginner materials' },
        { value: 'notsure', label: 'Not sure yet',                  desc: 'I might want to in the future' }
      ]
    },
    {
      id: 'setup',
      title: 'How important is easy setup?',
      hint: 'Some printers are true plug-and-play, others need tinkering',
      multi: false,
      cols: 2,
      options: [
        { value: 'easy',   label: 'Plug-and-play',    desc: 'Unbox, load filament, print' },
        { value: 'tinker', label: 'I can tinker',      desc: 'I\u2019m okay with some calibration' }
      ]
    }
  ];

  /* ── Filament recommendations by use case ─────────────────────────────── */
  var FILAMENT_REC = {
    toys:        [
      { name: 'PLA+', reason: 'Easy to print, great detail for figurines', link: '' },
      { name: 'Silk PLA', reason: 'Shiny metallic finish for display pieces', link: '' }
    ],
    functional:  [
      { name: 'PETG', reason: 'Strong, flexible, heat-resistant', link: '' },
      { name: 'PLA+', reason: 'Good strength for indoor functional parts', link: '' }
    ],
    prototyping: [
      { name: 'PLA+', reason: 'Cheap, fast, reliable - perfect for iterations', link: '' },
      { name: 'PETG', reason: 'When you need a tougher test piece', link: '' }
    ],
    art:         [
      { name: 'Silk PLA', reason: 'Beautiful metallic finish for décor', link: '' },
      { name: 'Matte PLA', reason: 'Smooth, elegant non-reflective surface', link: '' }
    ],
    education:   [
      { name: 'PLA+', reason: 'Safe, odorless, easy - ideal for classrooms', link: '' }
    ],
    _default:    [
      { name: 'PLA+', reason: 'The go-to filament for all beginners', link: '' }
    ]
  };

  // Engineering answer = Yes: the printer is enclosed, so suggest what the enclosure is for
  var FILAMENT_ENGINEERING = [
    { name: 'ABS', reason: 'Heat-resistant, tough parts', link: '' },
    { name: 'ASA', reason: 'Like ABS, and UV-proof for outdoor parts', link: '' },
    { name: 'CF filaments', reason: 'Very stiff parts (PLA-CF, PETG-CF, PA-CF); hardened steel nozzle needed', link: '', q: 'cf' }
  ];

  // Each suggestion opens its filament collection; Matte has none, so it searches
  var FILAMENT_COLLECTION = {
    'PLA+': '/collections/standard-pla-filaments',
    'Silk PLA': '/collections/exotic-pla-filaments',
    'PETG': '/collections/engineering-filaments',
    'ABS': '/collections/engineering-filaments',
    'ASA': '/collections/engineering-filaments',
    'CF filaments': '/collections/engineering-filaments'
  };
  function filamentLink(f) {
    return FILAMENT_COLLECTION[f.name] || '/search?type=product&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=show&q=' + encodeURIComponent(f.q || f.name.toLowerCase());
  }

  // "You'll also want": the how-it-works guides (<guides page>#<id>) that matter for these answers.
  // Each guide lists its accessories with Add to cart. Labels follow the guide titles on that page.
  var GUIDES = {
    'which-ams': 'Which AMS fits your printer',
    'nozzles': 'Nozzles for carbon fiber and more',
    'dry-filament': 'Keep your filament dry',
    'inserts-magnets': 'Brass inserts and magnets',
    'build-plates': 'Choose your build plate',
    'maintenance': 'Unclog and maintain your printer'
  };

  /* ── Printer knowledge helpers ────────────────────────────────────────── */
  // Enclosed: the Printer Enclosure metafield (Open / Enclosed); keyword guess only when it's empty
  function isEnclosed(p) {
    if (p.enclosure) return p.enclosure.toLowerCase() === 'enclosed';
    var t = (p.title + ' ' + p.description).toLowerCase();
    return t.indexOf('enclos') !== -1 || t.indexOf('enclosed') !== -1
      || t.indexOf('chamber') !== -1 || t.indexOf('carbon') !== -1
      || t.indexOf('p1s') !== -1 || t.indexOf('p2s') !== -1
      || t.indexOf('x1') !== -1 || t.indexOf('h2') !== -1;
  }

  // Bambu Lab printers and Elegoo's Centauri Carbon line are plug-and-play (auto-calibration out of the box)
  function isPlugAndPlay(p) {
    return (p.vendor || '').toLowerCase() === 'bambu lab' || (p.title || '').toLowerCase().indexOf('centauri carbon') !== -1;
  }

  // Size rank from the Build Volume category metafield, or from the largest build-volume side
  var SIZE_RANK = { small: 1, medium: 2, large: 3, xl: 4 };
  function sizeRank(p) {
    var r = SIZE_RANK[(p.sizeCat || '').toLowerCase()];
    if (r) return r;
    var mm = parseBuildVolumeMax(p.buildVolume);
    return mm === 0 ? 2 : mm <= 230 ? 1 : mm <= 300 ? 2 : mm <= 360 ? 3 : 4;
  }

  /* ── HTML escape helper ───────────────────────────────────────────────── */
  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Parse build volume string → max dimension in mm ──────────────────── */
  function parseBuildVolumeMax(str) {
    if (!str) return 0;
    // Common formats: "256×256×256mm", "256 x 256 x 256 mm", "256*256*256"
    var nums = String(str).match(/(\d+(?:\.\d+)?)/g);
    if (!nums || nums.length === 0) return 0;
    var max = 0;
    for (var i = 0; i < nums.length; i++) {
      var n = parseFloat(nums[i]);
      if (n > max) max = n;
    }
    return max;
  }

  /* ── Parse price cents ────────────────────────────────────────────────── */
  function parseCents(str) {
    var n = parseInt(str, 10);
    return isNaN(n) ? 0 : n;
  }

  /* ── Web Component ────────────────────────────────────────────────────── */
  class GetStartedQuiz extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.currentStep = 0;
      this.answers = {};
      this.waNumber = this.dataset.whatsappNumber || '';
      this.waMessage = this.dataset.whatsappMessage || '';
      this.guidesUrl = this.dataset.guidesUrl || '';
    }

    // Up to 3 guides, most specific first: multi-color on a Bambu pick, engineering materials, functional parts
    guidesFor(picks) {
      var a = this.answers;
      var has = function (q, v) { return (a[q] || []).indexOf(v) !== -1; };
      var bambu = picks.some(function (pk) { return (pk.p.vendor || '').toLowerCase() === 'bambu lab'; });
      var ids = [];
      if (has('multicolor', 'yes') && bambu) ids.push('which-ams');
      if (has('materials', 'yes')) ids.push('nozzles', 'dry-filament');
      if (has('use', 'functional') || has('use', 'prototyping')) ids.push('inserts-magnets');
      ids.push('build-plates', 'maintenance', 'dry-filament');
      var seen = {};
      return ids.filter(function (id) {
        if (seen[id] || !GUIDES[id]) return false;
        seen[id] = true;
        return true;
      }).slice(0, 3);
    }

    connectedCallback() {
      this.loadProducts();
      this.bodyEl = this.querySelector('#gs-quiz-body');
      this.resultsEl = this.querySelector('#gs-quiz-results');
      this.renderQuestion();
      this.initStickyCTA();
    }

    loadProducts() {
      var els = document.querySelectorAll('#gs-product-data .gs-printer');
      var arr = [];
      els.forEach(function (el) {
        arr.push(Object.assign({}, el.dataset));
      });
      this.products = arr;
    }

    /* ── Sticky CTA bar ─────────────────────────────────────────────────── */
    initStickyCTA() {
      var cta = document.getElementById('gs-sticky-cta');
      var btn = document.getElementById('gs-sticky-btn');
      var quizSection = document.getElementById('gs-quiz-section');
      if (!cta || !quizSection) return;

      var self = this;
      // The WhatsApp bubble sits in the same corner: body.gs-cta-visible lifts it above the bar
      function setVisible(on) {
        cta.classList.toggle('is-hidden', !on);
        cta.setAttribute('aria-hidden', on ? 'false' : 'true');
        document.body.classList.toggle('gs-cta-visible', on);
      }

      // Show CTA after user scrolls 300px
      var showed = false;
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) setVisible(false);
          else if (showed) setVisible(true);
        });
      }, { threshold: 0.1 });

      observer.observe(quizSection);

      // Show after scrolling past intro
      window.addEventListener('scroll', function () {
        if (window.scrollY > 400 && !showed) {
          showed = true;
          setVisible(true);
        }
      }, { passive: true });

      if (btn) {
        btn.addEventListener('click', function () {
          quizSection.scrollIntoView({ behavior: 'smooth' });
        });
      }
    }

    /* ── Render question ────────────────────────────────────────────────── */
    renderQuestion() {
      var q = QUESTIONS[this.currentStep];
      var self = this;
      var selected = this.answers[q.id] || [];
      var totalSteps = QUESTIONS.length;
      var stepNum = this.currentStep + 1;
      var pct = Math.round((stepNum / totalSteps) * 100);

      var html = '';

      // Progress bar
      html += '<div class="gs-quiz__progress">';
      html += '<p class="gs-quiz__progress-text">Step ' + stepNum + ' of ' + totalSteps + '</p>';
      html += '<div class="gs-quiz__progress-bar"><div class="gs-quiz__progress-fill" style="width:' + pct + '%"></div></div>';
      html += '</div>';

      // Question
      html += '<div class="gs-quiz__question">';
      html += '<h3 class="gs-quiz__question-title">' + esc(q.title) + '</h3>';
      if (q.hint) html += '<p class="gs-quiz__question-hint">' + esc(q.hint) + '</p>';
      html += '</div>';

      // Options
      var colsClass = q.cols === 2 ? ' gs-quiz__options--cols-2' : '';
      html += '<div class="gs-quiz__options' + colsClass + '">';
      q.options.forEach(function (opt) {
        var isSel = selected.indexOf(opt.value) !== -1;
        html += '<div class="gs-quiz__option' + (isSel ? ' is-selected' : '') + '" data-value="' + esc(opt.value) + '" role="button" tabindex="0" aria-pressed="' + isSel + '">';
        html += '<div class="gs-quiz__option-check">';
        html += '<svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5L4.5 8.5L11 1" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        html += '</div>';
        html += '<div>';
        html += '<div class="gs-quiz__option-label">' + esc(opt.label) + '</div>';
        if (opt.desc) html += '<div class="gs-quiz__option-desc">' + esc(opt.desc) + '</div>';
        html += '</div>';
        html += '</div>';
      });
      html += '</div>';

      // Navigation
      html += '<div class="gs-quiz__nav">';
      if (this.currentStep > 0) {
        html += '<button class="gs-quiz__btn gs-quiz__btn--back" type="button" id="gs-prev">';
        html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>';
        html += ' Back</button>';
      }
      var isLast = this.currentStep === totalSteps - 1;
      var nextLabel = isLast ? 'See Results' : 'Next';
      html += '<button class="gs-quiz__btn gs-quiz__btn--next" type="button" id="gs-next"' + (selected.length === 0 ? ' disabled' : '') + '>';
      html += nextLabel + ' ';
      html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>';
      html += '</button>';
      html += '</div>';

      this.bodyEl.innerHTML = html;
      this.resultsEl.classList.remove('is-visible');
      this.resultsEl.innerHTML = '';

      // Bind option clicks
      this.bodyEl.querySelectorAll('.gs-quiz__option').forEach(function (el) {
        var handler = function () {
          var val = el.dataset.value;
          var q2 = QUESTIONS[self.currentStep];
          var arr = self.answers[q2.id] || [];

          if (q2.multi) {
            var idx = arr.indexOf(val);
            if (idx === -1) { arr.push(val); }
            else { arr.splice(idx, 1); }
          } else {
            arr = [val];
          }
          self.answers[q2.id] = arr;
          self.renderQuestion();
        };

        el.addEventListener('click', handler);
        el.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(); }
        });
      });

      // Bind nav buttons
      var prevBtn = this.bodyEl.querySelector('#gs-prev');
      var nextBtn = this.bodyEl.querySelector('#gs-next');

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          self.currentStep--;
          self.renderQuestion();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if ((self.answers[QUESTIONS[self.currentStep].id] || []).length === 0) return;
          if (self.currentStep < totalSteps - 1) {
            self.currentStep++;
            self.renderQuestion();
          } else {
            self.showResults();
          }
        });
      }
    }

    /* ── Calculate & show results ───────────────────────────────────────── */
    showResults() {
      var a = this.answers;
      var RANGES = { 'u400': [0, 40000], '400-600': [40000, 60000], '600-1200': [60000, 120000], '1200+': [120000, Infinity] };
      var range = RANGES[(a.budget || [])[0]] || RANGES['u400'];
      var wantMulti = (a.multicolor || [])[0] === 'yes';
      var sizeChoice = (a.size || [])[0] || 'small';
      var minSize = sizeChoice === 'large' ? 3 : sizeChoice === 'medium' ? 2 : 1;
      var eng = (a.materials || [])[0] || 'no';
      var wantEasy = (a.setup || [])[0] === 'easy';
      var uses = a.use || [];

      // Hard answers rule printers out; budget is a cap (a cheaper printer is still allowed).
      function fits(p, check) {
        var c = parseCents(p.priceCents);
        if (check.budget && c > range[1]) return false;
        if (check.size && sizeRank(p) < minSize) return false;
        if (wantMulti && p.multiColor !== 'true') return false;
        if (eng === 'yes' && !isEnclosed(p)) return false;
        return true;
      }
      var cents = function (p) { return parseCents(p.priceCents); };
      var inStock = function (p) { return p.available === 'true'; };
      var cheapestFirst = function (x, y) { return inStock(y) - inStock(x) || cents(x) - cents(y); };
      var priced = this.products.filter(function (p) { return cents(p) > 0; });

      priced.forEach(function (p) {
        var c = cents(p), score = 0;
        var multi = p.multiColor === 'true', enc = isEnclosed(p), pnp = isPlugAndPlay(p), size = sizeRank(p);
        score += c >= range[0] ? 20 : 8;                       // in the chosen range beats far cheaper
        score += wantEasy ? (pnp ? 15 : 0) : (pnp ? 8 : 10);
        score += wantMulti ? 10 : (multi ? 2 : 10);            // no extra cost for color nobody asked for
        score += size === minSize ? 10 : size === minSize + 1 ? 6 : 2;
        score += eng === 'yes' ? 10 : eng === 'notsure' ? (enc ? 6 : 2) : 4;
        var uc = 0;
        uses.forEach(function (u) {
          if (u === 'functional' && enc) uc += 4;
          if ((u === 'art' || u === 'toys') && multi) uc += 3;
          if (u === 'education' && pnp) uc += 4;
          if (u === 'prototyping') uc += 2;
        });
        score += Math.min(uc, 10);
        if (inStock(p)) score += 12;                           // strong preference, not a filter
        p._score = score;
      });

      var picks = [];
      var used = {};
      var note = '';
      function take(p, label) { if (p && !used[p.handle]) { used[p.handle] = true; picks.push({ p: p, label: label }); } }
      var unused = function (p) { return !used[p.handle]; };

      var pool = priced.filter(function (p) { return fits(p, { budget: true, size: true }); });
      if (pool.length) {
        pool.sort(function (x, y) { return y._score - x._score || cents(x) - cents(y); });
        var best = pool[0];
        take(best, 'Best match');
        if (!inStock(best)) take(pool.filter(function (p) { return inStock(p) && unused(p); })[0], 'Best you can get today');
        // Lower price = a cheaper printer that still matches the setup and size answers (in stock first), then the
        // nearest price; Step up = the nearest dearer one. Pass 4: a $1,699 Klipper XL printer beat the P2S Combo here.
        var setupMatch = function (p) { return !wantEasy || isPlugAndPlay(p) ? 1 : 0; };
        var sizeFit = function (p) { var s = sizeRank(p); return s === minSize ? 2 : s === minSize + 1 ? 1 : 0; };
        if (picks.length < 3) take(pool.filter(function (p) { return unused(p) && cents(p) < cents(best); })
          .sort(function (x, y) {
            return inStock(y) - inStock(x) || setupMatch(y) - setupMatch(x) || sizeFit(y) - sizeFit(x) || cents(y) - cents(x);
          })[0], 'Lower price');
        if (picks.length < 3) take(pool.filter(function (p) { return unused(p) && cents(p) > cents(best); }).sort(cheapestFirst)[0], 'Step up');
        if (picks.length < 3) take(pool.filter(unused).sort(function (x, y) { return inStock(y) - inStock(x) || y._score - x._score; })[0], 'Also a good fit');

        if (!picks.some(function (x) { return inStock(x.p); })) {
          // Nothing at this price can be bought today: say so, and show the closest printer that can
          var today = priced.filter(function (p) {
            return unused(p) && inStock(p) && cents(p) > range[1] && cents(p) <= range[1] * 2 && fits(p, { size: true });
          }).sort(cheapestFirst)[0];
          take(today, 'Closest you can buy today');
          note = today ? 'Nothing at this price is in stock right now. The closest printer you can buy today is shown too.'
                       : 'Nothing at this price is in stock right now. Ask us on WhatsApp when it arrives.';
        } else if (picks.length < 2 && isFinite(range[1])) {
          // Only one printer fits: offer the closest one just above the budget, clearly labelled
          take(priced.filter(function (p) {
            return unused(p) && cents(p) > range[1] && cents(p) <= range[1] * 1.25 && fits(p, { size: true });
          }).sort(cheapestFirst)[0], 'Just over your budget');
        }
      } else {
        // Nothing fits the budget: the closest printers above it, cheapest first, never more than twice the budget
        var closest = priced.filter(function (p) { return fits(p, { size: true }) && cents(p) <= range[1] * 2; }).sort(cheapestFirst);
        note = 'Nothing fits all your answers within your budget, so these are the closest above it.';
        if (!closest.length) {
          closest = priced.filter(function (p) { return fits(p, { size: true }); }).sort(cheapestFirst);
        }
        if (!closest.length) {
          closest = priced.filter(function (p) { return fits(p, {}); }).sort(cheapestFirst);
          note = 'Nothing fits all your answers, so these are the closest matches.';
        }
        take(closest[0], 'Closest to your budget');
        take(closest[1], 'Next closest');
        take(closest[2], 'Next closest');
      }

      this.renderResults(picks, note, eng === 'yes' ? FILAMENT_ENGINEERING : null);
    }

    renderResults(picks, note, engineeringFilaments) {
      this.bodyEl.innerHTML = ''; // hide quiz
      var resultsEl = this.resultsEl;
      resultsEl.classList.add('is-visible');

      // Filament suggestions: the engineering set when the answer was Yes, otherwise by use case
      var filamentList = engineeringFilaments;
      if (!filamentList) {
        var filaments = {};
        (this.answers.use || ['_default']).forEach(function (uc) {
          (FILAMENT_REC[uc] || FILAMENT_REC._default).forEach(function (r) { if (!filaments[r.name]) filaments[r.name] = r; });
        });
        filamentList = Object.keys(filaments).map(function (k) { return filaments[k]; });
        if (!filamentList.length) filamentList = FILAMENT_REC._default;
      }

      var html = '';
      html += '<h2 class="gs-results__heading">Your Recommendations</h2>';
      html += '<p class="gs-results__sub">Based on your answers, here’s what we think is the best fit.</p>';
      if (note) html += '<p class="gs-results__note">' + esc(note) + '</p>';

      if (!picks.length) {
        html += '<div class="gs-results__empty">';
        html += '<p class="gs-results__empty-text">We couldn’t find a match with those answers. Try changing one, or chat with our team for advice.</p>';
        html += '</div>';
      } else {
        html += '<div class="gs-results__tiers">';
        var self = this;
        picks.forEach(function (pick, i) {
          var now = pick.label === 'Best you can get today' || pick.label === 'Closest you can buy today';
          html += self.renderTierCard(pick.p, i === 0 ? 'value' : (now ? 'now' : 'budget'), pick.label, filamentList);
        });
        html += '</div>';
        if (document.getElementById('gs-filament-quiz-section')) {
          html += '<p class="gs-results__filament-cta"><a href="#gs-filament-quiz-section">Pick your filament with the filament quiz ↓</a></p>';
        }
        var guides = this.guidesUrl ? this.guidesFor(picks) : [];
        if (guides.length) {
          var guidesUrl = this.guidesUrl;
          html += '<div class="gs-results__guides">';
          html += '<p class="gs-results__guides-title">You’ll also want</p>';
          html += '<ul class="gs-results__guides-list">';
          guides.forEach(function (id) {
            html += '<li><a href="' + esc(guidesUrl + '#' + id) + '">' + esc(GUIDES[id]) + ' →</a></li>';
          });
          html += '</ul></div>';
        }
      }

      // WhatsApp CTA
      var waNum = this.waNumber;
      var waMsg = this.waMessage;
      if (waNum) {
        html += '<div class="gs-whatsapp">';
        html += '<h3 class="gs-whatsapp__heading">Still not sure?</h3>';
        html += '<p class="gs-whatsapp__text">Our 3D printing experts are happy to help you choose the perfect setup.</p>';
        html += '<a class="gs-whatsapp__btn" href="https://wa.me/' + esc(waNum) + '?text=' + esc(waMsg) + '" target="_blank" rel="noopener">';
        html += '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.75.75 0 00.917.918l4.462-1.493A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.29 0-4.408-.763-6.104-2.05a.75.75 0 00-.63-.132l-3.065 1.025 1.027-3.072a.75.75 0 00-.131-.63A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>';
        html += ' Chat on WhatsApp</a>';
        html += '</div>';
      }

      // Restart button
      html += '<div class="gs-quiz__restart">';
      html += '<button class="gs-quiz__restart-btn" type="button" id="gs-restart">';
      html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>';
      html += ' Retake Quiz</button>';
      html += '</div>';

      resultsEl.innerHTML = html;

      // Once there are results, the sticky bar leads back to them instead of offering the quiz again
      var ctaText = document.querySelector('.gs-sticky-cta__text');
      var ctaLabel = document.querySelector('.gs-sticky-cta__label');
      if (ctaText) ctaText.textContent = 'Your printer picks are ready';
      if (ctaLabel) ctaLabel.textContent = 'See your results';

      // The filament quiz below opens with "Now that you've found your printer...": only true from here on
      var transition = document.querySelector('.gs-fq__transition');
      if (transition) transition.classList.add('is-visible');

      // Add to cart without leaving the page, so the other picks and the filament advice stay on screen
      resultsEl.querySelectorAll('.gs-result-tier__buy').forEach(function (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var btn = form.querySelector('button');
          btn.disabled = true;
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(form.querySelector('[name="id"]').value), quantity: 1 }], sections: 'cart-icon-bubble' })
          }).then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); }).then(function (res) {
            var bubble = document.getElementById('cart-icon-bubble');
            var html = res.sections && res.sections['cart-icon-bubble'];
            if (bubble && html) {
              var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
              if (fresh) bubble.innerHTML = fresh.innerHTML;
            }
            form.outerHTML = '<a class="gs-result-tier__added" href="/cart">Added to your cart - View cart</a>';
          }).catch(function () { form.submit(); });
        });
      });

      // Bind restart
      var me = this;
      var restartBtn = resultsEl.querySelector('#gs-restart');
      if (restartBtn) {
        restartBtn.addEventListener('click', function () {
          me.currentStep = 0;
          me.answers = {};
          me.renderQuestion();
          document.getElementById('gs-quiz-section').scrollIntoView({ behavior: 'smooth' });
        });
      }
    }

    renderTierCard(product, tierClass, tierLabel, filaments) {
      var AVAIL_CLASS = { 'Coming Soon': 'badge--coming', 'Pre-Order': 'badge--preorder', 'Back-Order': 'badge--backorder' };
      var html = '';
      html += '<div class="gs-result-tier gs-result-tier--' + tierClass + '">';
      html += '<span class="gs-result-tier__label">' + esc(tierLabel) + '</span>';
      html += '<div class="gs-result-tier__body">';

      html += '<div class="gs-result-tier__img">';
      if (product.image) {
        html += '<img src="' + esc(product.image) + '" alt="' + esc(product.title) + '" loading="lazy" width="220" height="180">';
      }
      html += '</div>';

      if (AVAIL_CLASS[product.avail]) {
        html += '<span class="badge ' + AVAIL_CLASS[product.avail] + '">' + esc(product.avail) + '</span>';
      }
      html += '<div class="gs-result-tier__vendor">' + esc(product.vendor) + '</div>';
      html += '<div class="gs-result-tier__name">' + esc(product.title) + '</div>';
      html += '<div class="gs-result-tier__price">' + esc(product.price) + '</div>';

      // In stock: add to cart straight from the result; otherwise the product page (Pre-Order / Coming Soon details)
      if (product.available === 'true' && product.variantId) {
        html += '<form class="gs-result-tier__buy" method="post" action="/cart/add">';
        html += '<input type="hidden" name="id" value="' + esc(product.variantId) + '">';
        html += '<input type="hidden" name="quantity" value="1">';
        html += '<button type="submit" class="gs-result-tier__add">Add to cart</button>';
        html += '</form>';
      }
      html += '<a class="gs-result-tier__link" href="' + esc(product.url) + '">';
      html += 'View Product ';
      html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>';
      html += '</a>';

      // Filament recommendations
      if (filaments.length > 0) {
        html += '<div class="gs-result-tier__filaments">';
        html += '<p class="gs-result-tier__filament-title">Recommended filaments</p>';
        filaments.forEach(function (f) {
          html += '<div class="gs-result-tier__filament-item">';
          html += '<span>•</span> ';
          html += '<a href="' + esc(filamentLink(f)) + '">' + esc(f.name) + '</a>';
          html += ' - ' + esc(f.reason);
          html += '</div>';
        });
        html += '</div>';
      }

      html += '</div>'; // body
      html += '</div>'; // tier
      return html;
    }
  }

  customElements.define('get-started-quiz', GetStartedQuiz);

}());
